import { computed, readonly, ref } from 'vue';
import axios from 'axios';
import { SettingService } from '@/service/SettingService';
import { setAppLocale } from '@/i18n';

export const DEFAULT_COMPANY_NAME = 'Express App';

/** Sakai / legacy Homa assets — never treat these as the company logo. */
const STATIC_BRAND_LOGO_PATTERN =
  /\/layout\/images\/(?:main-logo|bw-main-logo|logo-dark|logo-white)(?:\.\w+)?(?:\?.*)?$/i;

const companySettings = ref({
  app_name: DEFAULT_COMPANY_NAME,
  trade_name: null,
  legal_name: null,
  logo_url: null,
  locale: 'en',
  calendar: 'gregorian'
});
const loading = ref(false);
const loaded = ref(false);
let pendingRequest = null;

const normalizedValue = (value) => (typeof value === 'string' ? value.trim() : '');

export const resolveCompanyName = (settings = {}) =>
  normalizedValue(settings.trade_name) ||
  normalizedValue(settings.app_name) ||
  normalizedValue(settings.legal_name) ||
  DEFAULT_COMPANY_NAME;

export const isStaticBrandLogoUrl = (value) => STATIC_BRAND_LOGO_PATTERN.test(normalizedValue(value));

/** Point relative/storage logo paths at the API origin so the SPA can load them. */
export const absolutizeCompanyLogoUrl = (value) => {
  const url = normalizedValue(value);
  if (!url || /^data:/i.test(url)) {
    return url || null;
  }

  const apiBase = normalizedValue(axios.defaults.baseURL).replace(/\/$/, '');
  if (!apiBase) {
    return url;
  }

  try {
    if (url.includes('/storage/')) {
      const parsed = /^https?:\/\//i.test(url) ? new URL(url) : null;
      const path = parsed ? `${parsed.pathname}${parsed.search}` : url.startsWith('/') ? url : `/${url}`;
      return new URL(path, `${apiBase}/`).href;
    }

    if (url.startsWith('/') && !url.startsWith('//')) {
      return new URL(url, `${apiBase}/`).href;
    }
  } catch {
    return url;
  }

  return url;
};

/** First usable company logo URL wins. Never invent a static branded asset. */
export const resolveCompanyLogoUrl = (...candidates) => {
  for (const candidate of candidates) {
    const value = normalizedValue(candidate);
    if (!value || isStaticBrandLogoUrl(value)) {
      continue;
    }

    return absolutizeCompanyLogoUrl(value);
  }

  return null;
};

const companyName = computed(() => resolveCompanyName(companySettings.value));
const legalCompanyName = computed(() => normalizedValue(companySettings.value.legal_name) || companyName.value);
const companyLogoUrl = computed(() => resolveCompanyLogoUrl(companySettings.value.logo_url));

const applyDocumentBrand = () => {
  if (typeof document !== 'undefined') {
    document.title = companyName.value;
  }
  setAppLocale(companySettings.value.locale);
};

const loadCompanySettings = async ({ force = false } = {}) => {
  if (loaded.value && !force) {
    applyDocumentBrand();
    return companySettings.value;
  }

  if (pendingRequest && !force) {
    return pendingRequest;
  }

  loading.value = true;
  pendingRequest = new SettingService()
    .getPublicSettings()
    .then((response) => {
      const data = response.data?.data;
      if (data) {
        companySettings.value = {
          ...companySettings.value,
          ...data
        };
      }
      loaded.value = true;
      applyDocumentBrand();
      return companySettings.value;
    })
    .catch(() => {
      applyDocumentBrand();
      return companySettings.value;
    })
    .finally(() => {
      loading.value = false;
      pendingRequest = null;
    });

  return pendingRequest;
};

export function useCompanyBrand() {
  return {
    settings: readonly(companySettings),
    companyName,
    legalCompanyName,
    companyLogoUrl,
    loading: readonly(loading),
    loaded: readonly(loaded),
    loadCompanySettings
  };
}
